import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { IconButton } from "@mui/material";

import { BankTransfer, Copy } from "@zenith/assets/icons/components";
import {
  EXPIRATION_DATE_FOR_PAYMENTS,
  EXPIRATION_DATE_FOR_PENDING_BANK_TRANSFER_ITEM,
  MILLISECONDS,
} from "@zenith/data/constants";
import { usePaymentsContext } from "@zenith/context/global-context/hooks";
import { formatCurrency, formatTimeForCounter } from "@zenith/utils";
import {
  ApiResponseDTOGenerateBankTransferDTO,
  GenerateApiService,
  OperationDTO,
} from "@zenith/services/transactions";
import { pollPaymentStatus } from "@zenith/utils/Polls/pollPaymentStatus";
import {
  PAYMENT_BANK_TRANSFER,
  PAYMENT_BANK_TRANSFER_PENDING,
  PAYMENT_OVERVIEW,
} from "@zenith/routes/paths";
import ZenithLogoRed from "@zenith/assets/icons/ZenithLogoRed";
import { ButtonComponent } from "@zenith/components";

const BankTransferPage = memo(function BankTransferPage() {
  const { paymentsContext, setPaymentsContext } = usePaymentsContext();
  const {
    totalAmount,
    amount,
    expirationDate,
    operationId,
    transactionInfo: { accountBank, accountName, accountNumber },
  } = paymentsContext;
  const navigate = useNavigate();

  const [copyFeedback, setCopyFeedback] = useState<string>("");
  const [isLoadingNewCode, setIsLoadingNewCode] = useState(false);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState<number>(0);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (paymentsContext.amount) {
      localStorage.setItem("amount", paymentsContext.amount.toString());
    } else if (paymentsContext.totalAmount) {
      localStorage.setItem(
        "totalAmount",
        paymentsContext.totalAmount.toString()
      );
    }
  }, [paymentsContext.amount, paymentsContext.totalAmount]);

  useEffect(() => {
    const storedAmount = localStorage.getItem("amount");
    const storedTotalAmount = localStorage.getItem("totalAmount");

    if (!paymentsContext.amount && storedAmount) {
      setPaymentsContext({
        ...paymentsContext,
        amount: parseFloat(storedAmount),
      });
    } else if (!paymentsContext.totalAmount && storedTotalAmount) {
      setPaymentsContext({
        ...paymentsContext,
        totalAmount: parseFloat(storedTotalAmount),
      });
    }
  }, [paymentsContext.amount, paymentsContext.totalAmount]);

  useEffect(() => {
    let storedOperationId = operationId as string | "";

    if (!storedOperationId) {
      storedOperationId = localStorage.getItem("operationId") || "";
    }

    if (!storedOperationId) {
      console.error("Operation ID is missing");
      navigate(PAYMENT_OVERVIEW);
      return;
    }

    localStorage.setItem("operationId", storedOperationId);

    const fetchBankTransferDetails = async () => {
      try {
        if (!storedOperationId) {
          throw new Error("Operation ID is missing");
        }
        const response: ApiResponseDTOGenerateBankTransferDTO =
          await GenerateApiService.getGenerateInBankTransfer(storedOperationId);

        const expirationDate = response.data?.expirationDate;

        setPaymentsContext({
          ...paymentsContext,
          operationId: storedOperationId,
          expirationDate: expirationDate,
          transactionInfo: {
            transactionDate: response.dateTime,
            paymentMethod: OperationDTO.paymentMethod.IN_BANK_TRANSFER,
            accountBank: response.data?.bankName,
            accountName: response.data?.accountName,
            accountNumber: response.data?.accountNumber,
          },
          amount: paymentsContext.amount,
          totalAmount: paymentsContext.totalAmount,
        });

        const initialRemainingSeconds = Math.floor(
          (new Date(expirationDate as string).getTime() -
            new Date().getTime()) /
            MILLISECONDS
        );
        setSeconds(initialRemainingSeconds);
        localStorage.setItem("seconds", initialRemainingSeconds.toString());
      } catch (error) {
        console.error("Failed to refetch bank transfer details:", error);
      }
    };

    fetchBankTransferDetails();
  }, [operationId]);

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining =
        new Date(expirationDate as string).getTime() - new Date().getTime();

      if (remaining >= 0) {
        setSeconds(Math.floor(remaining / MILLISECONDS));
        localStorage.setItem(
          "seconds",
          Math.floor(remaining / MILLISECONDS).toString()
        );
      } else {
        clearInterval(timer);
        localStorage.removeItem("seconds");
      }
    }, 100);

    return () => clearInterval(timer);
  }, [expirationDate]);

  const handleCopyAccountNumber = () => {
    if (!accountNumber) return;

    const textArea = document.createElement("textarea");
    textArea.value = accountNumber;
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const feedback = successful ? "Account number copied!" : "Copy failed.";
      setCopyFeedback(feedback);
      setTimeout(() => setCopyFeedback(""), 3000);
    } catch (err) {
      console.error("Copy failed: ", err);
      setCopyFeedback("Copy failed.");
      setTimeout(() => setCopyFeedback(""), 3000);
    }

    document.body.removeChild(textArea);
  };

  const handleCopyAllDetails = () => {
    const codeToCopy = `Bank: ${accountBank}\nAccount Number: ${accountNumber}\nAccount Name: ${accountName}`;
    const textArea = document.createElement("textarea");
    textArea.value = codeToCopy;
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const feedback = successful ? "Details copied successfully!" : "Copy failed.";
      setCopyFeedback(feedback);
      setTimeout(() => setCopyFeedback(""), 3000);
    } catch (err) {
      console.error("Copy failed: ", err);
      setCopyFeedback("Copy failed.");
      setTimeout(() => setCopyFeedback(""), 3000);
    }

    document.body.removeChild(textArea);
  };

  const handleConfirmPayment = async () => {
    if (seconds <= 0 || !operationId) return;

    setIsConfirming(true);
    try {
      const response = await pollPaymentStatus(operationId, false);

      setPaymentsContext({
        ...paymentsContext,
        ...response.data,
        paymentStatus: response.status,
        operationDetails: response.data,
      });

      if (response.status === OperationDTO.status.SUCCESS) {
        navigate(PAYMENT_BANK_TRANSFER_PENDING, {
          state: {
            prevPath: PAYMENT_BANK_TRANSFER,
            initialStatus: OperationDTO.status.SUCCESS,
          },
        });
      } else {
        const expirationDateForBankTransfer =
          Date.now() + EXPIRATION_DATE_FOR_PAYMENTS;
        localStorage.setItem(
          EXPIRATION_DATE_FOR_PENDING_BANK_TRANSFER_ITEM,
          expirationDateForBankTransfer.toString()
        );

        navigate(PAYMENT_BANK_TRANSFER_PENDING, {
          state: {
            prevPath: PAYMENT_BANK_TRANSFER,
            initialStatus: response.status,
            errorMessage: response.error?.message,
          },
        });
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleGenerateNewBankTransfer = () => {
    if (isLoadingNewCode) return;
    if (error) setError("");

    setIsLoadingNewCode(true);

    if (!operationId) {
      setError("Operation ID is missing");
      setIsLoadingNewCode(false);
      return;
    }

    GenerateApiService.getGenerateInBankTransfer(operationId)
      .then((response: ApiResponseDTOGenerateBankTransferDTO) => {
        const expirationDate = response.data?.expirationDate;

        setPaymentsContext({
          ...paymentsContext,
          expirationDate: expirationDate,
          transactionInfo: {
            transactionDate: response.dateTime,
            paymentMethod: OperationDTO.paymentMethod.IN_BANK_TRANSFER,
            accountBank: response.data?.bankName,
            accountName: response.data?.accountName,
            accountNumber: response.data?.accountNumber,
          },
        });

        setIsLoadingNewCode(false);
      })
      .catch((error) => {
        console.error(error.message);
        setError(error.message);
        setIsLoadingNewCode(false);
      });
  };

  return (
    <div>
      <Button
        id="back-bank-transfer-link"
        onClick={() => {
          navigate(PAYMENT_OVERVIEW, {
            state: { prevPath: PAYMENT_BANK_TRANSFER },
          });
        }}
        className="text-base font-normal normal-case text-neutral-600 hover:bg-white"
      >
        <ArrowBackIosIcon fontSize="small" /> Back
      </Button>

      <div className="mt-12 mb-16 flex justify-center">
        <div className="flex w-120 flex-col rounded-2.5xl border border-neutral-200 px-8 pt-6 pb-8">
          <h1 className="mb-4 text-2xl font-medium text-neutral-900">
            Pay with Transfer Details
          </h1>
          <p className="mb-12 text-sm font-normal text-neutral-500">
            {seconds > 0
              ? "Inform the customer to make payment (transfer) to the account below."
              : ""}
          </p>
          <hr className="mb-8 border-neutral-200"></hr>
          <div className="mb-8 flex justify-between">
            <h2 className="text-base font-normal text-neutral-400">Amount</h2>
            <h2 className="text-main-200-925 text-2xl font-medium">
              {paymentsContext.orderItems &&
              paymentsContext.orderItems.length === 0
                ? formatCurrency(amount)
                : formatCurrency(totalAmount)}
            </h2>
          </div>
          {seconds > 0 ? (
            <>
              <div className="mb-4 flex flex-row rounded-2xl border border-neutral-200 py-8 font-medium text-neutral-900">
                <div className="flex w-full flex-col px-6">
                  <ZenithLogoRed height={52} width={52} color="#E03E32" />
                  <hr className="mt-4 h-0 w-full border border-gray-300"></hr>
                  <div className="flex h-20 flex-col items-start justify-start gap-2 self-stretch pt-1">
                    <div className="inline-flex items-start justify-between self-stretch">
                      <div className="leading-normal text-zinc-600">Bank</div>
                      <div className="text-right text-zinc-900">
                        {accountBank}
                      </div>
                    </div>
                    <div className="inline-flex items-start justify-between self-stretch">
                      <div className="text-zinc-600">Account Number</div>
                      <div className="flex items-center justify-start gap-2">
                        <div className="text-right text-zinc-900">
                          {accountNumber}
                        </div>
                        <IconButton
                          size="small"
                          onClick={handleCopyAccountNumber}
                          className="p-0"
                        >
                          <Copy width={16} height={16} />
                        </IconButton>
                      </div>
                    </div>
                    <div className="inline-flex items-start justify-between self-stretch">
                      <div className="text-zinc-600">Account Name</div>
                      <div className="text-right text-zinc-900">
                        {accountName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-16 flex flex-col items-center">
                <Button
                  onClick={handleCopyAllDetails}
                  className="text-sm font-normal normal-case text-neutral-600"
                >
                  Copy All Details
                </Button>
                {copyFeedback && (
                  <p className="mt-2 text-sm text-green-600">{copyFeedback}</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex h-[358px] flex-col items-center justify-center">
              <div className="mb-6 flex h-[120px] w-[120px] items-center justify-center rounded-full bg-primary-300/50">
                <BankTransfer width={80} height={80} />
              </div>
              <p className="text-neutral-600">The account number has expired</p>
            </div>
          )}
          <div className="flex flex-col items-center">
            {seconds > 0 ? (
              <p className="mb-8 text-sm font-medium text-neutral-600">
                The above account number is valid for{" "}
                <span className="text-neutral-900">
                  {formatTimeForCounter(seconds)}
                </span>
              </p>
            ) : (
              <div className="mb-8">
                <Button
                  variant="text"
                  id="clear-cart-button"
                  className={`text-base font-normal normal-case ${
                    isLoadingNewCode
                      ? "cursor-not-allowed text-neutral-200"
                      : "text-neutral-600 hover:text-neutral-900"
                  } underline hover:bg-white hover:underline`}
                  disableRipple={isLoadingNewCode}
                  onClick={handleGenerateNewBankTransfer}
                >
                  Get New Account Number
                </Button>

                <ButtonComponent
                  label="Get New Account Number"
                  onClick={handleGenerateNewBankTransfer}
                  id="account-number-button"
                  size="large"
                  type="secondary"
                  disabled={isLoadingNewCode}
                  isLoading={isConfirming}
                  className="px-24"
                />
                {error && (
                  <p className="text-center text-sm text-primary-300">
                    {error}
                  </p>
                )}
              </div>
            )}

            <ButtonComponent
              label="Confirm Payment"
              onClick={handleConfirmPayment}
              id="confirm-payment-button"
              size="large"
              type="primary"
              disabled={seconds <= 0 || isConfirming}
              isLoading={isConfirming}
              className="px-24"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default BankTransferPage;